import { HttpErrorResponse } from '@angular/common/http';
import {
	Component,
	EventEmitter,
	Inject,
	Output,
	ChangeDetectorRef,
	computed,
	DestroyRef,
	inject,
	input,
	OnDestroy,
	signal
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import type { ConfirmDialogData } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import type { GetLicenseHolderInvitesResponse, LicenseHolder, LicenseHolderInvite, LicenseHolderOrInvite, SalesforceLicense } from '@go-modules/interfaces/licenses';
import { isInvite } from '@go-modules/interfaces/licenses/license-holder-or-invite';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { LicenseService } from '@go-modules/services/license/license.service';
import { EMPTY, Observable, Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { InviteUserDialogComponent } from './invite-user-dialog/invite-user-dialog.component';
import type { LicenseHoldersAndInvitesResponse } from '@go-modules/services/license/license.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import type { PaginationMeta } from '@go-modules/interfaces';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

const REMOVE_HOLDER_TITLE = 'license-management_confirmation-remove-holder-title';
const REMOVE_HOLDER_MESSAGE = 'license-management_confirmation-remove-holder-message';
const REMOVE_HOLDER_BUTTON = 'license-management_confirmation-remove-holder-button';

export const REMOVE_HOLDER_SUCCESS = 'license-management_confirmation-remove-success';
export const NETWORK_LOST_TRANSLATION_KEY = 'license-management_error-network-lost';
export const UNKNOWN_ERROR_TRANSLATION_KEY = 'license-management_error-unknown-error';
const LICENSE_HOLDERS_PAGE_SIZE = 50;
const LICENSE_INVITES_PAGE_SIZE = 50;

@Component({
	selector: 'license-holder',
	templateUrl: './license-holder.component.html',
	styleUrls: ['./license-holder.component.scss'],
	providers: [TranslatePipe],
	standalone: false
})
export class LicenseHolderComponent implements OnDestroy {
	public readonly salesforceLicense = input.required<SalesforceLicense>();
	@Output() public viewSeatsEvent = new EventEmitter<string>();
	@Output() public loadFailed = new EventEmitter<void>();

	private componentDestroyed$$ = new Subject();
	public readonly holdersLoading = signal(true);
	public readonly invitesLoading = signal(true);
	public readonly holdersPageIndex = signal(0);
	public readonly invitesPageIndex = signal(0);
	public readonly holdersPaginationMeta = signal<PaginationMeta | null>(null);
	public readonly invitesPaginationMeta = signal<PaginationMeta | null>(null);
	public readonly licenseHolders = signal<LicenseHolder[]>([]);
	public readonly licenseHolderInvites = signal<LicenseHolderInvite[]>([]);
	public readonly holdersExpanded = signal(true);
	public readonly invitesExpanded = signal(true);
	public readonly canAdministerLicense = computed(() => {
		if (this.userService.currentUser.is_root_user
			|| this.salesforceLicense().is_org_admin
			|| this.salesforceLicense().is_license_admin
		) {
			return true;
		}

		const myHolder = this.licenseHolders()
			.find((holder) => holder.user?.user_id === this.userService.currentUser.user_id);

		return Boolean(myHolder?.is_admin);
	});
	private readonly destroyRef = inject(DestroyRef);
	public holdersPageSize = LICENSE_HOLDERS_PAGE_SIZE;
	public invitesPageSize = LICENSE_INVITES_PAGE_SIZE;

	public constructor (
		@Inject(userServiceToken) private userService: UserService,
		public auth: NgxAuthService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private translationPipe: TranslatePipe,
		private licenseService: LicenseService,
		private snackBar: MatSnackBar,
		private cdr: ChangeDetectorRef) {
		toObservable(this.salesforceLicense).pipe(
			map((license) => license.license_id),
			distinctUntilChanged(),
			tap(() => {
				this.resetPagination();
				this.loadLicenseHolders(0);
				this.loadLicenseHolderInvites(0);
			}),
			takeUntilDestroyed(this.destroyRef)
		).subscribe();
	}

	private static handleRemoveUserError (err: HttpErrorResponse): Observable<string> {
		if (err.status === 0) {
			return of(NETWORK_LOST_TRANSLATION_KEY);
		}

		return of(UNKNOWN_ERROR_TRANSLATION_KEY);
	}

	public ngOnDestroy (): void {
		this.componentDestroyed$$.next(void(0));
		this.componentDestroyed$$.complete();
	}

	public canUpdateHolder (holder: LicenseHolderOrInvite): boolean {
		return this.userService.currentUser.is_root_user ||
			(!this.getAccountOwner(holder) && this.getHolderUserId(holder) !== this.userService.currentUser.user_id);
	}

	public canRemoveHolder (holder: LicenseHolderOrInvite): boolean {
		return this.userService.currentUser.is_root_user ||
			(!this.getAccountOwner(holder) && this.getHolderUserId(holder) !== this.userService.currentUser.user_id);
	}

	public permissionChange (event: MatSelectChange, holder: LicenseHolderOrInvite) {
		if (event.value !== holder.is_admin) {
			let confirmData: ConfirmDialogData;
			if(holder.is_admin) {
				confirmData = {
					title: this.translationPipe.transform('license-management_confirmation-permision-to-instructor-title'),
					message: this.translationPipe.transform('license-management_confirmation-permision-to-instructor-message'),
					confirmText: this.translationPipe.transform('license-management_confirmation-permision-to-instructor-confirm-button')
				};
			} else {
				confirmData = {
					title: this.translationPipe.transform('license-management_confirmation-permision-to-admin-title'),
					message: this.translationPipe.transform('license-management_confirmation-permision-to-admin-message'),
					confirmText: this.translationPipe.transform('license-management_confirmation-permision-to-admin-confirm-button')
				};
			}

			const dialogRef = ConfirmDialogComponent.open(this.dialog, { data: confirmData });
			const isAdminRollback = holder.is_admin;

			dialogRef.afterClosed()
				.pipe(
					take(1),
					takeUntil(this.componentDestroyed$$),
					filter((value) => {
						if (value) {
							event.source.disabled = true;
							holder.is_admin = event.value;
							return true;
						}

						event.source.writeValue(holder.is_admin);
						return false;
					}),
					switchMap(() => {
						return isInvite(holder) ?
							this.licenseService.updateLicenseHolderInvite(holder.license_id, holder) :
							this.licenseService.updateLicenseHolder(holder.license_id, holder);
					}),
					finalize(() => event.source.disabled = false)
				)
				.subscribe({
					next: () => {
						this.snackBar.open(this.translationPipe.transform('license-management_permission-update-success-message'), null, { duration: 1000 });
					},
					error: (httpError: HttpErrorResponse) => {
						holder.is_admin = isAdminRollback;
						event.source.writeValue(isAdminRollback);
						this.snackBar.open(httpError.error.message, null, { duration: 1000 });
					}
				});
		}
	}

	public addUsers () {
		const dialog$ = InviteUserDialogComponent.open(this.dialog, {data: {
			salesforceLicense: this.salesforceLicense()
		}}).afterClosed();

		dialog$.pipe(
			filter((holdersAndInvites: LicenseHoldersAndInvitesResponse | undefined) => holdersAndInvites != null),
			takeUntil(this.componentDestroyed$$)
		).subscribe((_holdersAndInvites: LicenseHoldersAndInvitesResponse) => {
			this.holdersPageIndex.set(0);
			this.invitesPageIndex.set(0);
			this.loadLicenseHolders(0);
			this.loadLicenseHolderInvites(0);
		});
	}

	public removeUser (holder: LicenseHolderOrInvite & {removing?: boolean}) {
		const removeHolderTranslationKeys = [
			REMOVE_HOLDER_TITLE,
			REMOVE_HOLDER_MESSAGE,
			REMOVE_HOLDER_BUTTON
		];
		this.translate.get(removeHolderTranslationKeys).pipe(
			map((translations) => ({
				title: translations[REMOVE_HOLDER_TITLE],
				message: translations[REMOVE_HOLDER_MESSAGE],
				confirmText: translations[REMOVE_HOLDER_BUTTON]
			})),
			switchMap((confirmData) => ConfirmDialogComponent.open(this.dialog, { data: confirmData }).afterClosed()),
			filter((confirm: boolean) => confirm),
			tap(() => {
				holder.removing = true;
				this.cdr.detectChanges();
			}),
			switchMap(() =>
				isInvite(holder) ?
					this.licenseService.removeLicenseHolderInvite(this.salesforceLicense().license_id, holder.id) :
					this.licenseService.removeLicenseHolder(this.salesforceLicense().license_id, holder.id)
			),
			tap(() => {
				const list = isInvite(holder) ? this.licenseHolderInvites() : this.licenseHolders();
				const index = list.findIndex((item) => item.id === holder.id);
				if (index >= 0) {
					const updatedList = list.slice(0);
					updatedList.splice(index, 1);
					this.updateLicenseLists(updatedList, isInvite(holder));
				}

				if (!isInvite(holder) && holder.seats_consumed != null) {
					this.salesforceLicense().total_seats_consumed -= holder.seats_consumed;
				}

				const isInviteList = isInvite(holder);
				this.updatePaginationTotal(-1, isInviteList);
				const updatedListLength = list.length - 1;

				if (updatedListLength === 0) {
					if (isInviteList && this.invitesPageIndex() > 0) {
						const newPageIndex = this.invitesPageIndex() - 1;
						this.invitesPageIndex.set(newPageIndex);
						this.loadLicenseHolderInvites(newPageIndex);
					}

					if (!isInviteList && this.holdersPageIndex() > 0) {
						const newPageIndex = this.holdersPageIndex() - 1;
						this.holdersPageIndex.set(newPageIndex);
						this.loadLicenseHolders(newPageIndex);
					}
				}
			}),
			map(() => REMOVE_HOLDER_SUCCESS),
			catchError(LicenseHolderComponent.handleRemoveUserError),
			switchMap((translationKey: string) => this.translate.get(translationKey)),
			finalize(() => {
				holder.removing = false;
			})
		).subscribe((translationString: string) => this.snackBar.open(translationString, null, { duration: 3000 }));
	}

	public viewSeats (holder: LicenseHolderOrInvite) {
		this.viewSeatsEvent.emit(this.getHolderEmail(holder));
	}

	public onHoldersPageChange (event: PageEvent): void {
		if (event.pageIndex === this.holdersPageIndex() || this.holdersLoading()) {
			return;
		}

		this.holdersPageIndex.set(event.pageIndex);
		this.loadLicenseHolders(event.pageIndex);
	}

	public onInvitesPageChange (event: PageEvent): void {
		if (event.pageIndex === this.invitesPageIndex() || this.invitesLoading()) {
			return;
		}

		this.invitesPageIndex.set(event.pageIndex);
		this.loadLicenseHolderInvites(event.pageIndex);
	}

	private loadLicenseHolders (pageIndex: number): void {
		if (this.salesforceLicense().license_id == null) {
			return;
		}

		this.holdersLoading.set(true);

		this.licenseService.getLicenseHolders(this.salesforceLicense().license_id, pageIndex + 1).pipe(
			takeUntil(this.componentDestroyed$$),
			finalize(() => {
				this.holdersLoading.set(false);
			}),
			catchError((err: HttpErrorResponse) => {
				this.handleLoadError(err);
				return EMPTY;
			})
		).subscribe((response) => {
			this.holdersPageSize = response.meta?.per_page ?? LICENSE_HOLDERS_PAGE_SIZE;
			this.holdersPaginationMeta.set(response.meta);
			this.licenseHolders.set(response.data ?? []);
		});
	}

	private loadLicenseHolderInvites (pageIndex: number): void {
		if (this.salesforceLicense().license_id == null) {
			return;
		}

		this.invitesLoading.set(true);

		this.licenseService.getLicenseHolderInvites(this.salesforceLicense().license_id, pageIndex + 1).pipe(
			takeUntil(this.componentDestroyed$$),
			finalize(() => {
				this.invitesLoading.set(false);
			}),
			catchError((err: HttpErrorResponse) => {
				this.handleInviteLoadError(err);
				return EMPTY;
			})
		).subscribe((response: GetLicenseHolderInvitesResponse) => {
			this.invitesPageSize = response.meta?.per_page ?? LICENSE_INVITES_PAGE_SIZE;
			this.invitesPaginationMeta.set(response.meta);
			this.licenseHolderInvites.set(response.data ?? []);
		});
	}

	private updateLicenseLists (updatedList: LicenseHolderOrInvite[], isInviteList: boolean): void {
		if (isInviteList) {
			const invites = updatedList as LicenseHolderInvite[];
			this.licenseHolderInvites.set(invites);
			return;
		}

		const holders = updatedList as LicenseHolder[];
		this.licenseHolders.set(holders);
	}

	private handleLoadError (err: HttpErrorResponse): void {
		const translationKey = err.status === 0 ? NETWORK_LOST_TRANSLATION_KEY : UNKNOWN_ERROR_TRANSLATION_KEY;
		this.translate.get(translationKey).pipe(take(1)).subscribe((translationString: string) => {
			this.snackBar.open(translationString, null, { duration: 3000 });
		});
		this.loadFailed.emit();
	}

	private handleInviteLoadError (err: HttpErrorResponse): void {
		const translationKey = err.status === 0 ? NETWORK_LOST_TRANSLATION_KEY : UNKNOWN_ERROR_TRANSLATION_KEY;
		this.translate.get(translationKey).pipe(take(1)).subscribe((translationString: string) => {
			this.snackBar.open(translationString, null, { duration: 3000 });
		});
	}

	private resetPagination (): void {
		this.holdersPageIndex.set(0);
		this.invitesPageIndex.set(0);
		this.holdersPaginationMeta.set(null);
		this.invitesPaginationMeta.set(null);
		this.licenseHolders.set([]);
		this.licenseHolderInvites.set([]);
	}

	public toggleHoldersExpanded (): void {
		this.holdersExpanded.set(!this.holdersExpanded());
	}

	public toggleInvitesExpanded (): void {
		this.invitesExpanded.set(!this.invitesExpanded());
	}

	private updatePaginationTotal (delta: number, isInviteList: boolean): void {
		const meta = isInviteList ? this.invitesPaginationMeta() : this.holdersPaginationMeta();
		if (meta == null) {
			return;
		}

		const perPage = isInviteList ? this.invitesPageSize : this.holdersPageSize;
		const total = Math.max(0, meta.total + delta);
		const lastPage = Math.max(1, Math.ceil(total / perPage));
		const updatedMeta = {
			...meta,
			total,
			last_page: lastPage
		};
		if (isInviteList) {
			this.invitesPaginationMeta.set(updatedMeta);
		} else {
			this.holdersPaginationMeta.set(updatedMeta);
		}
	}

	private getHolderEmail (holder: LicenseHolderOrInvite): string {
		return isInvite(holder) ? holder.email : holder.user?.email;
	}

	private getHolderUserId (holder: LicenseHolderOrInvite): number | null {
		return isInvite(holder) ? null : holder.user?.user_id ?? null;
	}

	private getAccountOwner (holder: LicenseHolderOrInvite): boolean {
		return isInvite(holder) ? false : Boolean(holder.account_owner);
	}
}
